import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ClientThemeDTO, ThemeClient, ThemeOverridesDTO, UserInfoDTO } from '../models/models.swagger.generated';
import { TenantThemeModeOverrides, TenantThemePayload, TenantThemeTokenOverrideMap } from './theme.model';
import { tenantThemeOverrideModes } from './tenant-theme-overrides.constants';
import { ThemeService } from './theme.service';
import { ToastService } from './toast.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class TenantThemeService {
  private readonly cacheStorageKey = 'app.theme.client.cache';

  constructor(
    private readonly themeClient: ThemeClient,
    private readonly themeService: ThemeService,
    private readonly toastService: ToastService,
    private readonly authService: AuthService
  ) {
  }

  /**
   * Applies the theme for the given (possibly logged-out) user.
   * - Not logged in: applies the last cached client theme (if any) so the branded colors
   *   are visible even before authentication.
   * - Logged in with a client: fetches the client's theme from the server, applies it and
   *   caches it, overriding any previously cached values.
   * - Logged in without a client (e.g. a global admin): clears any cached theme.
   */
  public initializeForUser(userInfo: UserInfoDTO | null): Observable<void> {
    if (!userInfo) {
      return this.applyCachedTheme();
    }

    const tenantId = userInfo.clientId ?? userInfo.client?.id ?? null;

    if (!tenantId) {
      this.clearCachedTenantTheme();
      this.themeService.applyTenantTheme('default', null);
      return of(void 0);
    }

    return this.applyForTenant(tenantId, true);
  }

  public applyForTenant(tenantId: number | string | null, cacheForNextVisit: boolean = false): Observable<void> {
    if (!tenantId) {
      this.themeService.applyTenantTheme('default', null);
      return of(void 0);
    }

    const numericTenantId = typeof tenantId === 'number'
      ? tenantId
      : Number.parseInt(tenantId, 10);

    if (Number.isNaN(numericTenantId)) {
      this.themeService.applyTenantTheme(String(tenantId), null);
      return of(void 0);
    }

    return this.themeClient.getClientTheme(numericTenantId)
      .pipe(
        map((response: ClientThemeDTO | null | undefined) => this.toTenantThemePayload(response)),
        tap((payload: TenantThemePayload | null) => {
          this.themeService.applyTenantTheme(String(tenantId), payload);
          if (cacheForNextVisit) {
            this.cacheTenantTheme(numericTenantId, payload);
          }
        }),
        map(() => void 0),
        catchError(() => {
          this.themeService.applyTenantTheme(String(tenantId), null);
          return of(void 0);
        })
      );
  }

  private applyCachedTheme(): Observable<void> {
    const cached = this.getCachedTenantTheme();

    if (!cached) {
      this.themeService.applyTenantTheme('default', null);
      return of(void 0);
    }

    this.themeService.applyTenantTheme(String(cached.tenantId), cached.payload);
    return of(void 0);
  }

  private cacheTenantTheme(tenantId: number, payload: TenantThemePayload | null): void {
    if (!payload) {
      this.clearCachedTenantTheme();
      return;
    }

    try {
      localStorage.setItem(this.cacheStorageKey, JSON.stringify({ tenantId, payload }));
    } catch {
      // localStorage may be unavailable (e.g. private browsing); ignore caching failures
    }
  }

  /**
   * Determines whether the given tenant is the currently logged-in user's own tenant.
   * Used to decide whether a theme edit should refresh the logged-out/login-page cache,
   * since an admin may be editing another client's theme without being logged out of it.
   */
  private isOwnTenant(tenantId: number): boolean {
    const userInfo = this.authService.getUserInfo();
    const ownTenantId = userInfo?.clientId ?? userInfo?.client?.id ?? null;
    return ownTenantId != null && Number(ownTenantId) === tenantId;
  }

  private clearCachedTenantTheme(): void {
    localStorage.removeItem(this.cacheStorageKey);
  }

  private getCachedTenantTheme(): { tenantId: number; payload: TenantThemePayload } | null {
    try {
      const raw = localStorage.getItem(this.cacheStorageKey);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as { tenantId?: number; payload?: TenantThemePayload };
      if (!parsed || typeof parsed !== 'object' || !parsed.payload || !parsed.tenantId) {
        return null;
      }

      return { tenantId: parsed.tenantId, payload: parsed.payload };
    } catch {
      return null;
    }
  }

  public saveForTenant(tenantId: number | null, payload: TenantThemePayload | null): Observable<TenantThemePayload | null> {
    if (!tenantId) {
      return of(null);
    }

    const savePayload = this.toClientThemeDto(payload);

    return this.themeClient.saveClientTheme(tenantId, savePayload)
      .pipe(
        map((response: ClientThemeDTO | null | undefined) => this.toTenantThemePayload(response)),
        tap((savedPayload: TenantThemePayload | null) => {
          this.themeService.applyTenantTheme(String(tenantId), savedPayload);
          if (this.isOwnTenant(tenantId)) {
            this.cacheTenantTheme(tenantId, savedPayload);
          }
          this.toastService.showSuccess(
            'i18n.domain.theme-showcase.tenantTheme.save.successTitle',
            'i18n.domain.theme-showcase.tenantTheme.save.successContent');
        }),
        catchError(err => {
          this.toastService.showApiException(
            err,
            'i18n.domain.theme-showcase.tenantTheme.save.errorTitle',
            'i18n.domain.theme-showcase.tenantTheme.save.errorContent');
          return throwError(() => err);
        })
      );
  }

  private toNumericTenantId(tenantId: number | string | null): number | null {
    if (!tenantId) {
      return null;
    }

    const parsedTenantId = typeof tenantId === 'number' ? tenantId : Number.parseInt(tenantId, 10);
    if (Number.isNaN(parsedTenantId) || parsedTenantId <= 0) {
      return null;
    }

    return parsedTenantId;
  }

  private toClientThemeDto(payload: TenantThemePayload | null): ClientThemeDTO {
    const dto = new ClientThemeDTO();

    dto.primary = payload?.primary?.trim() || undefined;
    dto.secondary = payload?.secondary?.trim() || undefined;
    dto.logoUrl = payload?.logoUrl?.trim() || undefined;
    dto.logoUrlDarkMode = payload?.logoUrlDarkMode?.trim() || undefined;

    const modeOverrides = this.toThemeOverridesDto(payload?.overrides);
    dto.overrides = modeOverrides;

    return dto;
  }

  private toThemeOverridesDto(overrides: TenantThemeModeOverrides | undefined): ThemeOverridesDTO | undefined {
    if (!overrides) {
      return undefined;
    }

    const dto = new ThemeOverridesDTO();
    dto.light = { ...(overrides.light ?? {}) };
    dto.dark = { ...(overrides.dark ?? {}) };

    const hasEntries = Object.keys(dto.light).length || Object.keys(dto.dark).length;
    return hasEntries ? dto : undefined;
  }

  private toTenantThemePayload(source: ClientThemeDTO | null | undefined): TenantThemePayload | null {
    if (!source) {
      return null;
    }

    const modeOverrides = this.toModeOverrides(source.overrides);

    const payload: TenantThemePayload = {
      primary: source.primary,
      secondary: source.secondary,
      logoUrl: source.logoUrl,
      logoUrlDarkMode: source.logoUrlDarkMode,
      overrides: modeOverrides
    };

    const hasAnyValue = payload.primary || payload.secondary || payload.logoUrl || payload.logoUrlDarkMode
      || Boolean(modeOverrides);
    return hasAnyValue ? payload : null;
  }

  private toModeOverrides(source: ThemeOverridesDTO | null | undefined): TenantThemeModeOverrides | undefined {
    if (!source) {
      return undefined;
    }

    const result: TenantThemeModeOverrides = {};

    tenantThemeOverrideModes.forEach((mode) => {
      const modeValue = source[mode] as unknown;
      const normalizedModeValue = this.toTokenMap(modeValue);
      if (normalizedModeValue) {
        result[mode] = normalizedModeValue;
      }
    });

    return Object.keys(result).length ? result : undefined;
  }

  private toTokenMap(source: unknown): TenantThemeTokenOverrideMap | undefined {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return undefined;
    }

    const entries = Object.entries(source as Record<string, unknown>)
      .filter((entry): entry is [string, string] => typeof entry[0] === 'string' && typeof entry[1] === 'string')
      .map(([key, value]) => [key, value.trim()] as const)
      .filter(([, value]) => Boolean(value));

    if (!entries.length) {
      return undefined;
    }

    return Object.fromEntries(entries);
  }
}
